import React, {useEffect, useState} from "react";
import Sidebar from "../components/Sidebar";
import AddButton from "../components/AddButton";

import {registerLocale} from "react-datepicker";
import {it} from "date-fns/locale";
import "react-datepicker/dist/react-datepicker.css";

import axios from "axios";
import {toast} from "react-toastify";
import {privateApi} from "../api";
import RefreshButton from "../components/RefreshButton";

registerLocale("it", it);

// ---- TYPES ----
type WorkTypeObject = {
    id: string;
    name: string;
}

type StatusResponse = {
    status: string;
    code: number;
    message: string;
    workTypes: WorkTypeObject[];
}

function WorkType() {
    localStorage.setItem("focus", "lavorazioni")
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [workTypes, setWorkTypes] = useState<WorkTypeObject[]>([]);
    const [selectedWorkTypeId, setSelectedWorkTypeId] = useState("");
    const [selectedWorkTypeName, setSelectedWorkTypeName] = useState("");
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [loading, setLoading] = useState(false);

    const [workTypeInput, setWorkTypeInput] = useState("");

    // ---- RESET ----
    const resetForm = () => {
        setWorkTypeInput("");
    };

    const loadWorkTypes = async () => {
        setLoading(true)
        try {
            const res = await privateApi.get<StatusResponse>("/cr-order-manager/v1/workType");

            setWorkTypes(res.data.workTypes ?? []);
        } catch (err) {
            if (axios.isAxiosError(err)) {
                const message =
                    err.response?.data?.message ||
                    "Errore lato server";
                toast.error(message);
            } else {
                toast.error("Errore non gestito");
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadWorkTypes();
    }, []);

    // ---- SUBMIT ----
    const handleSubmit = async () => {
        if (workTypeInput.trim() === "") return toast.error("Inserisci una lavorazione valida");

        const payload = {
            description: workTypeInput
        };

        try {
            await privateApi.post("/cr-order-manager/v1/workType", payload);

            setIsModalOpen(false);
            resetForm();
            loadWorkTypes();
        } catch (err) {
            if (axios.isAxiosError(err)) {
                const message =
                    err.response?.data?.message ||
                    "Errore lato server";
                toast.error(message);
            } else {
                toast.error("Errore non gestito");
            }
        }
    };

    const handleEdit = async () => {
        if (selectedWorkTypeName.trim() === "") return toast.error("Inserisci una lavorazione valida");

        const payload = {
            workTypeId: selectedWorkTypeId,
            description: selectedWorkTypeName
        };

        try {
            await privateApi.patch("/cr-order-manager/v1/workType", payload);

            setIsEditModalOpen(false);
            loadWorkTypes();
        } catch (err) {
            if (axios.isAxiosError(err)) {
                const message =
                    err.response?.data?.message ||
                    "Errore lato server";
                toast.error(message);
            } else {
                toast.error("Errore non gestito");
            }
        }
    };

    const handleDelete = async () => {
        if (selectedWorkTypeName.trim() === "") return toast.error("Inserisci una lavorazione valida");

        try {
            await privateApi.delete(`/cr-order-manager/v1/workType/${selectedWorkTypeId}`);

            setIsEditModalOpen(false);
            loadWorkTypes();
        } catch (err) {
            if (axios.isAxiosError(err)) {
                const message =
                    err.response?.data?.message ||
                    "Errore lato server";
                toast.error(message);
            } else {
                toast.error("Errore non gestito");
            }
        }
    };

    return (
        <>
            <Sidebar/>

            <main className="ml-64 p-6">
                <div className="flex items-center justify-between mb-6">
                    <h1 className="text-2xl font-bold">Lavorazioni</h1>
                    <div className="flex items-center gap-2">
                        <RefreshButton onClick={loadWorkTypes}/>
                        <AddButton
                            onClick={() => {
                                resetForm();
                                setIsModalOpen(true);
                            }}
                        />
                    </div>
                </div>

                <div className="space-y-6">
                    <div className="bg-white rounded-xl shadow p-5">

                        {/*Table*/}
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm text-left">

                                <thead className="text-gray-500 border-b">
                                <tr>
                                    <th className="py-2">ID</th>
                                    <th className="py-2">Nome</th>
                                </tr>
                                </thead>

                                <tbody className="divide-y">
                                {loading ? (
                                    <tr>
                                        <td
                                            colSpan={4}
                                            className="py-6 text-center text-gray-500"
                                        >
                                            Caricamento...
                                        </td>
                                    </tr>
                                ) : workTypes.length === 0 ? (
                                    <tr>
                                        <td
                                            colSpan={4}
                                            className="py-6 text-center text-gray-500"
                                        >
                                            Nessuna lavorazione trovata
                                        </td>
                                    </tr>
                                ) : (workTypes.map((workType: WorkTypeObject) => (
                                    <tr key={workType.id} className="hover:bg-gray-50"
                                        onClick={() => {
                                            setSelectedWorkTypeId(workType.id);
                                            setSelectedWorkTypeName(workType.name);
                                            setIsEditModalOpen(true);
                                        }}>
                                        <td className="py-2">{workType.id}</td>
                                        <td className="py-2">{workType.name}</td>
                                    </tr>
                                )))}

                                </tbody>

                            </table>
                        </div>

                    </div>
                </div>

                {isEditModalOpen && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center">
                        <div className="bg-white rounded-xl p-6 w-full max-w-md">
                            <h2 className="text-xl font-bold mb-4">
                                Modifica Lavorazione
                            </h2>

                            <div className="space-y-3">
                                <input className="w-full border p-2 rounded"
                                       placeholder="Nome lavorazione"
                                       value={selectedWorkTypeName}
                                       onChange={(e) => {
                                           setSelectedWorkTypeName(e.target.value);
                                       }}/>
                            </div>

                            <div className="flex justify-end gap-2 mt-4">
                                <button
                                    onClick={handleDelete}
                                    className="px-4 py-2 text-white bg-red-700 rounded"
                                >
                                    Elimina
                                </button>
                                <button
                                    onClick={() => {
                                        setIsEditModalOpen(false);
                                    }}
                                    className="px-4 py-2 bg-gray-200 rounded"
                                >
                                    Annulla
                                </button>

                                <button
                                    onClick={handleEdit}
                                    className="px-4 py-2 bg-blue-900 text-white rounded"
                                >
                                    Salva
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {isModalOpen && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center">
                        <div className="bg-white rounded-xl p-6 w-full max-w-md">
                            <h2 className="text-xl font-bold mb-4">
                                Nuovo Lavorazione
                            </h2>

                            <div className="space-y-3">
                                <input className="w-full border p-2 rounded"
                                       placeholder="Nome lavorazione"
                                       value={workTypeInput}
                                       onChange={(e) => {
                                           setWorkTypeInput(e.target.value);
                                       }}/>
                            </div>

                            <div className="flex justify-end gap-2 mt-4">
                                <button
                                    onClick={() => {
                                        setIsModalOpen(false);
                                        resetForm();
                                    }}
                                    className="px-4 py-2 bg-gray-200 rounded"
                                >
                                    Annulla
                                </button>

                                <button
                                    onClick={handleSubmit}
                                    className="px-4 py-2 bg-blue-900 text-white rounded"
                                >
                                    Salva
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </main>
        </>
    )
        ;
}

export default WorkType;